import React, { useEffect, useState, useMemo } from 'react';
import './SidebarMenuItem.scss';
import { Icon, Typography } from '@design-system';
import { MenuItemColor, MenuItemsType } from '@utils/menu';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMenuContext } from '../SidebarMenu';

type SidebarMenuProps = {
    item: MenuItemsType;
    isActive?: boolean;
    color: MenuItemColor;
    intrinsic: 'h3' | 'span';
};

/**
 * Vérifie si un chemin correspond à un pattern avec paramètres
 * Exemple: '/members-management/members/:id/details/preview' correspond à '/members-management/members/7895/details/preview'
 */
const isPathMatching = (currentPath: string, patternPath: string): boolean => {
    // Si les chemins sont identiques, c'est un match direct
    if (currentPath === patternPath) {
        return true;
    }

    // Diviser les chemins en segments
    const currentSegments = currentPath.split('/').filter(Boolean);
    const patternSegments = patternPath.split('/').filter(Boolean);

    // Si le nombre de segments est différent, pas de match
    if (currentSegments.length !== patternSegments.length) {
        return false;
    }

    // Comparer chaque segment
    for (let i = 0; i < patternSegments.length; i++) {
        const patternSegment = patternSegments[i];
        const currentSegment = currentSegments[i];

        // Si le segment du pattern commence par ':', c'est un paramètre
        if (patternSegment.startsWith(':')) {
            // Le paramètre doit avoir une valeur (pas vide)
            if (!currentSegment) {
                return false;
            }
        } else {
            // Sinon, les segments doivent être identiques
            if (patternSegment !== currentSegment) {
                return false;
            }
        }
    }

    return true;
};

/**
 * Vérifie si le chemin actuel correspond à un élément de menu
 */
const isItemActive = (
    currentPath: string,
    itemUrl?: string,
    subPaths?: string[],
    activePaths?: string[]
): boolean => {
    // Vérifier l'URL directe
    if (itemUrl && currentPath === itemUrl) {
        return true;
    }

    // Vérifier les sous-chemins
    if (subPaths && itemUrl) {
        const hasSubPathMatch = subPaths.some(subPath =>
            currentPath.includes(`${itemUrl}/${subPath}`)
        );
        if (hasSubPathMatch) {
            return true;
        }
    }

    // Vérifier les activePaths avec support des paramètres
    if (activePaths) {
        const hasActivePathMatch = activePaths.some(activePath =>
            isPathMatching(currentPath, activePath)
        );
        if (hasActivePathMatch) {
            return true;
        }
    }

    return false;
};



export const SidebarMenuItem: React.FC<SidebarMenuProps> = ({
    item,
    isActive = false,
    color,
    intrinsic
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { openMenus, closeAllMenusExcept, toggleMenu } = useMenuContext();
    const [currentPath, setCurrentPath] = useState<string>(location.pathname);

    // Générer un ID unique pour ce menu basé sur le label
    const menuId = item.label;

    // Mettre à jour le chemin actuel quand la location change
    useEffect(() => {
        setCurrentPath(location.pathname);
    }, [location]);

    // Calculer si l'élément parent est actif
    const isParentActive = useMemo(() => {
        if (!item.children) {
            return isItemActive(currentPath, item.url, item.subPath, item.activePath);
        }

        return item.children.some(child =>
            isItemActive(currentPath, child.url, undefined, child.activePath)
        );
    }, [currentPath, item]);

    // Calculer si un enfant est actif
    const hasActiveChild = useMemo(() => {
        if (!item.children) {
            return false;
        }

        return item.children.some(child =>
            isItemActive(currentPath, child.url, undefined, child.activePath)
        );
    }, [currentPath, item.children]);

    // Déterminer si le menu doit être ouvert
    const isOpen = useMemo(() => {
        // Si un enfant est actif, toujours ouvrir
        if (hasActiveChild) {
            return true;
        }

        // Sinon, vérifier l'état manuel via le contexte
        return openMenus.has(menuId);
    }, [hasActiveChild, openMenus, menuId]);

    // Gérer le clic sur l'élément de menu
    const handleItemClick = () => {
        if (item.url) {
            // Si l'élément a une URL, fermer tous les autres menus sauf celui-ci et naviguer
            closeAllMenusExcept(menuId);
            navigate(item.url);
        } else if (item.children) {
            // Si l'élément a des enfants, toggle l'état d'ouverture
            toggleMenu(menuId);
        }
    };

    // Gérer le clic sur un enfant
    const handleChildClick = (childUrl: string) => {
        if (childUrl) {
            // Fermer tous les autres menus sauf le parent de cet enfant
            closeAllMenusExcept(menuId);
            navigate(childUrl);
        }
    };

    // Déterminer l'icône à afficher
    const shouldShowActiveIcon = isParentActive || isActive;
    const iconToShow = shouldShowActiveIcon ? item.activeIconSrc : item.iconSrc;

    return (
        <>
            <div
                key={item.label}
                className={`menu-item ${item.isLocked ? 'locked' : ''} ${isParentActive || isActive ? 'active' : ''}`}
                data-color={color}
                onClick={handleItemClick}
                style={{ cursor: 'pointer' }}
            >
                <Icon
                    icon={iconToShow}
                    size={20}
                    color={color}
                />

                <Typography
                    intrinsic={intrinsic}
                    variant={!isParentActive && !isActive ? 'secondary' : undefined}
                    color={color}
                    className="menu-item-label"
                >
                    {item.label}
                </Typography>

                {item.count && (
                    <Typography
                        intrinsic="span"
                        variant="p2"
                        color="white"
                        className="menu-item-count"
                    >
                        {item.count}
                    </Typography>
                )}
            </div>

            {isOpen && item.children && (
                <div className="menu-item-children" data-color={item.childrenColor}>
                    {item.children.map((child) => {
                        const isChildActive = isItemActive(
                            currentPath,
                            child.url,
                            undefined,
                            child.activePath
                        );

                        return (
                            <div
                                key={child.label}
                                className={`menu-item-child ${child.isLocked ? 'locked' : ''} ${isChildActive ? 'active' : ''}`}
                                data-maincolor={color}
                                onClick={() => handleChildClick(child.url)}
                            >
                                <Typography
                                    intrinsic="p"
                                    variant={!isChildActive ? 'secondary' : undefined}
                                    color={color}
                                    className="menu-item-child-label"
                                >
                                    {child.label}
                                </Typography>
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
};
